import { RWMockQuestion } from '../data/mockExamRWQuestions';

interface RWQuestionNavigatorProps {
  questions: RWMockQuestion[];
  currentIndex: number;
  userAnswers: (string | null)[];
  onJump: (index: number) => void;
  flagged: boolean[];
  onToggleFlag: (index: number) => void;
}

export default function RWQuestionNavigator({
  questions,
  currentIndex,
  userAnswers,
  onJump,
  flagged,
  onToggleFlag,
}: RWQuestionNavigatorProps) {
  const answered = userAnswers.filter((a) => a !== null).length;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-5 sticky top-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 text-sm">Question Navigator</h3>
        <span className="text-xs text-gray-500 font-medium">{answered}/54 answered</span>
      </div>

      {/* Domain legend */}
      <div className="space-y-1 mb-4">
        {[
          { label: 'Information & Ideas', color: 'bg-violet-200' },
          { label: 'Craft & Structure', color: 'bg-rose-200' },
          { label: 'Expression of Ideas', color: 'bg-amber-200' },
          { label: 'Standard English', color: 'bg-teal-200' },
        ].map((d) => (
          <div key={d.label} className="flex items-center gap-2">
            <span className={`inline-block w-3 h-3 rounded-sm ${d.color}`} />
            <span className="text-xs text-gray-600 truncate">{d.label}</span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-6 gap-1.5 mb-4">
        {questions.map((q, i) => {
          const isAnswered = userAnswers[i] !== null;
          const isCurrent = i === currentIndex;
          const isFlagged = flagged[i];

          const domainBorder =
            q.domain === 'Information & Ideas'
              ? 'border-violet-300'
              : q.domain === 'Craft & Structure'
              ? 'border-rose-300'
              : q.domain === 'Expression of Ideas'
              ? 'border-amber-300'
              : 'border-teal-300';

          let base =
            'relative w-full aspect-square rounded-lg text-xs font-bold flex items-center justify-center cursor-pointer transition-all border-2 ';

          if (isCurrent) {
            base += 'border-violet-500 bg-violet-500 text-white shadow-md scale-110 z-10';
          } else if (isFlagged) {
            base += 'border-amber-400 bg-amber-50 text-amber-700';
          } else if (isAnswered) {
            base += `${domainBorder} bg-violet-50 text-violet-700`;
          } else {
            base += 'border-gray-200 bg-gray-50 text-gray-500 hover:border-violet-300 hover:bg-violet-50';
          }

          return (
            <button key={i} onClick={() => onJump(i)} className={base} title={`Q${i + 1}: ${q.domain}`}>
              {isFlagged && !isCurrent && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full" />
              )}
              {i + 1}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => onToggleFlag(currentIndex)}
        className={`w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap ${
          flagged[currentIndex]
            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        <i className={`ri-flag-${flagged[currentIndex] ? 'fill' : 'line'}`} />
        {flagged[currentIndex] ? 'Unflag Question' : 'Flag for Review'}
      </button>

      <div className="mt-4 space-y-1.5 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-violet-50 border-2 border-violet-300 inline-block" />
          Answered
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-gray-50 border-2 border-gray-200 inline-block" />
          Unanswered
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-amber-50 border-2 border-amber-400 inline-block" />
          Flagged
        </div>
      </div>
    </div>
  );
}
